import * as PIXI from 'pixi.js';
import { SceneImageRenderMode } from '../enums';
import { ErrorReporter } from './error-reporter';

export type RenderSceneImageResult = HTMLCanvasElement;

export type TaskCallbackOptions = RenderSceneImageResult[] | Error;

export type RenderSceneImageTask = {
    sceneId: string;
    subsceneIndex: number;
    relTime: number;
    targets: HTMLCanvasElement[];
    mode: SceneImageRenderMode;
    abortSignal?: AbortSignal;
};

export const DEFAULT_SCENE_IMAGE_RENDER_MODE = SceneImageRenderMode.FRAME;

type InternalTask = RenderSceneImageTask & {
    promise: Promise<void>;
    resolve: () => void;
    reject: (error: Error) => void;
};

export class RenderQueue extends PIXI.utils.EventEmitter {
    classType = 'RenderQueue';
    name = 'RenderQueue_1';

    // data structure holding all the work
    #tasks: InternalTask[] = [];

    // is a task currently being processed?
    #taskInProgress = false;

    // is the queue currently processing tasks?
    #queueInProgress = false;

    // @internal if the queue is allowed to process tasks
    _canProcess = false;

    // the function that's going to be called for each task entry
    workFunction:
        | ((task: RenderSceneImageTask, done: (options?: TaskCallbackOptions) => void) => void)
        | null = null;

    // @internal the data used while processing the current task
    _currentWork: InternalTask | null = null;

    #onTaskDonePromise = Promise.resolve();
    #onUnlockPromise = Promise.resolve();

    /**
     * Process all existing tasks
     */
    startProcessingTasks() {
        if (this.#queueInProgress) {
            return;
        }
        this.#queueInProgress = true;

        const processTasksSequentially = async () => {
            while (!this.isEmpty() && !this.locked() && !this.taskInProgress()) {
                try {
                    await this.processTask();
                } catch (error) {
                    ErrorReporter.capture(error);
                }
            }
            this.#queueInProgress = false;
            this.emit('allTasksDone');
        };
        void processTasksSequentially();
    }

    queueInProgress() {
        return this.#queueInProgress;
    }

    static compareWork(work1: RenderSceneImageTask, work2: RenderSceneImageTask): boolean {
        return (
            work1.sceneId === work2.sceneId &&
            work1.subsceneIndex === work2.subsceneIndex &&
            work1.relTime === work2.relTime &&
            work1.mode === work2.mode
        );
    }

    #findDuplicate(taskToMatch: RenderSceneImageTask) {
        return this.#tasks.find((existingTask) => {
            return RenderQueue.compareWork(existingTask, taskToMatch);
        });
    }

    /**
     * Returns true if the queue is working on a task
     */
    taskInProgress(): boolean {
        return this.#taskInProgress;
    }

    async #onTaskDone() {
        if (this.#taskInProgress) {
            await new Promise<void>((resolve) => {
                this.once('taskDone', resolve);
            });
        }
    }

    /**
     * Resolves once the Queue has finished working on the current task
     */
    async onTaskDone(): Promise<void> {
        this.#onTaskDonePromise = this.#onTaskDonePromise.then(async () => {
            await this.#onTaskDone();
        });
        return this.#onTaskDonePromise;
    }

    /**
     * Process the next task
     */
    async processTask(): Promise<void> {
        // make sure we actually can process the next task
        if (this.locked() || this.taskInProgress() || this.isEmpty()) {
            return;
        }

        const currentInternalTask = this.dequeue();
        if (!currentInternalTask) {
            return;
        }
        const currentExternalTask: RenderSceneImageTask = {
            sceneId: currentInternalTask.sceneId,
            subsceneIndex: currentInternalTask.subsceneIndex,
            relTime: currentInternalTask.relTime,
            targets: currentInternalTask.targets,
            mode: currentInternalTask.mode,
        };

        const onTaskDoneCallback = (options?: TaskCallbackOptions) => {
            this._currentWork = null;
            this.#taskInProgress = false;

            // resolve current task
            let success = true;
            if (options instanceof Error) {
                success = false;
                this.emit('taskFailed', currentExternalTask, options);
                currentInternalTask.reject(options);
            } else {
                this.emit('taskSuccess', currentExternalTask);
                currentInternalTask.resolve();
            }
            this.emit('taskDone', currentExternalTask, success);
        };

        // append the taskDone callback function to our args
        if (this.workFunction) {
            this._currentWork = currentInternalTask;
            this.#taskInProgress = true;

            this.emit('taskStart', this._currentWork);
            this.workFunction(currentExternalTask, onTaskDoneCallback);
        } else {
            throw Error(`${this.name}: Unable to process task. No work function has been defined.`);
        }

        return currentInternalTask.promise;
    }

    /**
     * Return true is the Queue is empty
     */
    isEmpty(): boolean {
        return this.#tasks.length === 0;
    }

    /**
     * Add a new task to the queue.
     * This is helpful to remove duplicate task.
     * but not that as soon as we get the generated image we resolve the promise of the duplicate task as well.
     * @returns a promise that resolves when the task is done
     */
    async enqueue(task: RenderSceneImageTask): Promise<void> {
        if (task.abortSignal?.aborted) {
            throw new Error('Render task aborted');
        }

        let taskResolve!: () => void;
        let taskReject!: (error: Error) => void;
        const promise = new Promise<void>((resolve, reject) => {
            taskResolve = resolve;
            taskReject = reject;
        });

        const duplicate = this.#findDuplicate(task);

        if (duplicate) {
            duplicate.targets.push(...task.targets);
            const resolve = duplicate.resolve;
            const reject = duplicate.reject;
            duplicate.resolve = () => {
                resolve();
                taskResolve();
            };
            duplicate.reject = (error) => {
                reject(error);
                taskReject(error);
            };
        } else {
            const taskToAdd = {
                ...task,
                resolve: taskResolve,
                reject: taskReject,
                promise,
            };
            this.#tasks.push(taskToAdd);

            task.abortSignal?.addEventListener('abort', () => {
                const taskIndex = this.#tasks.indexOf(taskToAdd);
                if (taskIndex === -1) {
                    return;
                }

                this.#tasks.splice(taskIndex, 1);
                taskReject(new Error('Render task aborted'));
            });
        }

        // restart the queue when this was the first task we added to the queue
        // and we can process it
        if (this.length === 1 && !this.locked()) {
            this.startProcessingTasks();
        }

        return promise;
    }

    dequeue(): InternalTask | undefined {
        return this.#tasks.shift();
    }

    get length(): number {
        return this.#tasks.length;
    }

    async clear() {
        // wait of the current task to complete, so we can safely clear all the items
        await this.lock();

        // clear all existing items
        this.#tasks = [];
    }

    async destroy() {
        await this.clear();
        this._canProcess = false;
        this.workFunction = null;
    }

    /**
     * Resolves when the queue is unlocked
     */
    async #onUnlock(): Promise<void> {
        if (this.locked()) {
            await new Promise<void>((resolve) => {
                this.once('unlocked', () => resolve);
            });
        }
    }

    /**
     * Resolves once the Queue is unlocked
     */
    async onUnlock(): Promise<void> {
        this.#onUnlockPromise = this.#onUnlockPromise.then(async () => {
            await this.#onUnlock();
        });
        return this.#onUnlockPromise;
    }

    /**
     * Lock the queue so it stops processing its tasks once it complete the existing one
     */
    async lock(): Promise<void> {
        this._canProcess = false;
        return this.onTaskDone().then(async () => {
            this.#queueInProgress = false;
        });
    }

    /**
     * Unlock the queue so it can start processing its tasks
     */
    unlock(andStart = true) {
        this._canProcess = true;
        this.emit('unlocked');
        if (andStart) {
            this.startProcessingTasks();
        }
    }

    /**
     * Determine if the queue is locked or not
     */
    locked(): boolean {
        return !this._canProcess;
    }

    /**
     * Remove all tasks for the given scene id.
     * @returns the number of tasks that were removed
     */
    dequeueTasksForScene(id: string): number {
        let count = 0;
        let i = this.length;
        while (i--) {
            if (this.#tasks[i].sceneId === id) {
                this.#tasks.splice(i, 1);
                count++;
            }
        }
        return count;
    }
}
